<template>
  <div
    v-if="!isDismissed"
    class="announce"
    :class="`announce--${config.variant || 'info'}`"
    :data-announce-key="config.key"
    :style="styleVars"
    role="status"
    aria-live="polite"
  >
    <div class="announce__inner">
      <span class="announce__emoji" aria-hidden="true">{{ config.emoji || 'ℹ️' }}</span>

<!-- Non-marquee -->
<div v-if="!config.marquee" class="announce__text">
  <template v-if="config.duo">
    <span class="announce__label"
          v-if="config.label_html"
          v-html="allowHtml(config.label_html)"></span>
    <span class="announce__label" v-else>{{ config.label }}</span>

    <span class="announce__desc"
          v-if="config.desc_html"
          v-html="allowHtml(config.desc_html)"></span>
    <span class="announce__desc" v-else>{{ config.desc }}</span>
  </template>
  <template v-else>
    <span v-html="allowHtml(config.html)"></span>
  </template>
</div>

<!-- Marquee -->
<div v-else class="announce__scroller">
  <div class="announce__track" :style="trackVars" ref="track">
    <div class="announce__chunk" ref="chunk1">
      <template v-if="config.duo">
        <span class="announce__label"
              v-if="config.label_html"
              v-html="allowHtml(config.label_html)"></span>
        <span class="announce__label" v-else>{{ config.label }}</span>

        <span class="announce__desc"
              v-if="config.desc_html"
              v-html="allowHtml(config.desc_html)"></span>
        <span class="announce__desc" v-else>{{ config.desc }}</span>
      </template>
      <template v-else>
        <span v-html="allowHtml(config.html)"></span>
      </template>
    </div>

    <!-- clone -->
    <div class="announce__chunk">
      <template v-if="config.duo">
        <span class="announce__label"
              v-if="config.label_html"
              v-html="allowHtml(config.label_html)"></span>
        <span class="announce__label" v-else>{{ config.label }}</span>

        <span class="announce__desc"
              v-if="config.desc_html"
              v-html="allowHtml(config.desc_html)"></span>
        <span class="announce__desc" v-else>{{ config.desc }}</span>
      </template>
      <template v-else>
        <span v-html="allowHtml(config.html)"></span>
      </template>
    </div>
  </div>
</div>

      <button
        v-if="config.dismissible"
        class="announce__close"
        aria-label="Dismiss announcement"
        title="Dismiss"
        @click="dismiss"
      >×</button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AnnouncementBar',
  props: {
    config: { type: Object, required: true }
  },
  data() {
    return {
      isDismissed: false,
      expiryTimer: null, 
     density: 1, 
      trackVars: {},       // CSS vars for marquee distance/duration
      resizeRaf: null
    };
  },
  computed: {
    storageKey() {
      const k = this.config?.key || 'default';
      return `pt_announce_${k}`;
    },
    styleVars() {
      // inline CSS vars for two-tone colors
      const vars = {};
      if (this.config?.label_color) vars['--ann-label'] = this.config.label_color;
  // Prefer per-config density, else use component fallback `this.density`, else 1
 const d = (this.config && this.config.density != null) ? this.config.density
          : (this.density != null ? this.density : 1);
  vars['--ann-density'] = String(d);
      return vars;
    }
  },
  mounted() {
    // Respect prior dismiss
    try {
      if (localStorage.getItem(this.storageKey) === '1') {
        this.isDismissed = true;
        return;
      }
    } catch (e) { /* no-op */ }

    // Auto-hide on expiry boundary (optional)
    const exp = this.config?.expires_at ? Date.parse(this.config.expires_at) : NaN;
    const now = Date.now();
    if (!Number.isNaN(exp) && exp > now) {
      this.expiryTimer = setTimeout(() => { this.isDismissed = true; }, (exp - now) + 250);
    }

    // Marquee setup
    this.$nextTick(() => {
      this.computeMarquee();
      window.addEventListener('resize', this.onResize, { passive: true });
    });
  },
  beforeUnmount() {
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    window.removeEventListener('resize', this.onResize);
    if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
  },
  methods: {
     allowHtml(html) {
    if (!html) return '';
    return html.replace(/<(?!\/?(a|b|strong|i|em|u|br)\b)[^>]*>/gi, '');
  },
    dismiss() {
      try { localStorage.setItem(this.storageKey, '1'); } catch (e) { /* no-op */ }
      this.isDismissed = true;
    },
    onResize() {
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => this.computeMarquee());
    },
    computeMarquee() {
      if (!this.config?.marquee) return;

      // Respect reduced motion
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.trackVars = { animation: 'none' };
        return;
      }

      const chunkEl = this.$refs.chunk1;
      if (!chunkEl) return;

      // measure content width
      const w = chunkEl.getBoundingClientRect().width || 0;
      const gapPx = 40;
      const speed = parseFloat(this.config.marquee_speed) || 80; // px/s
      const distance = -(w + gapPx);
      const duration = Math.max(4, (w + gapPx) / speed);         // clamp to min 4s

      this.trackVars = {
        '--marquee-to': `${distance}px`,
        '--marquee-duration': `${duration}s`
      };
    }
  }
};
</script>

<style scoped>
/* =====================
   Announcement Bar
====================== */
.announce{
    --ann-density: 1;          /* 1 = normal; 0.8 = 20% shorter (height only) */
  --ann-vpad: .6rem;         /* vertical padding base */
  --ann-hpad: 20px;          /* horizontal padding base */
  --ann-font: .95rem;        /* base font size */
  --ann-line: 1.25;          /* base line-height */
  position:relative;
  border-bottom:1px solid rgba(255,255,255,.12);
  box-shadow:0 6px 18px rgba(0,0,0,.18);
 color:var(--ann-ink, #eaf2ff);
font-size: var(--ann-font);
  background:
    radial-gradient(260px 160px at 12% 100%, var(--ann-accent, rgba(28,95,151,.28)), transparent 60%),
    radial-gradient(200px 160px at 90% 0%, rgba(255,255,255,.06), transparent 60%),
    linear-gradient(180deg, var(--ann-bg1, rgba(11,27,43,.96)), var(--ann-bg2, rgba(15,38,61,.94)));
}
.announce__inner{
  display:flex; align-items:center; gap:.75rem;
  /* match landing: 0 side pad comes from .container (20px),
     so emulate that directly here */
  /* Density only affects vertical size */
  min-height: calc(48px * var(--ann-density));
  padding: calc(var(--ann-vpad) * var(--ann-density)) var(--ann-hpad);
 max-width:1120px; margin:0 auto;
  margin:0 auto;
 }
.announce__emoji{font-size: 1.15rem; line-height:1}  /* keep emoji size constant */
.announce__text{font-weight:600}
.announce__text{
  font-weight:600;
  line-height:1.25;     /* keep rows tight */
}
.announce__text{font-weight:600}
.announce a.lnk{
  color:inherit; text-decoration:underline;
  text-underline-offset:2px;
}
.announce__desc a { text-decoration: underline; text-underline-offset: 2px; }
/* Make links inside v-html look like the front page */
.announce__desc :deep(a),
:deep(.announce__desc a),
/* Vue 2 fallbacks */
/* Vue 3 SFC (scoped): style links injected via v-html */
.announce__desc :deep(a) {
  color: var(--ann-desc, currentColor);
  text-decoration: underline;
  text-underline-offset: 2px;
}

/* optional hover tweak */
.announce__desc :deep(a:hover) {
  text-decoration-thickness: from-font;
}


 .announce__close{
   margin-left:auto; appearance:none; border:0; background:transparent; color:inherit;
    display:inline-flex; align-items:center; justify-content:center;
  font-size: 1.1rem;           /* keep × size constant */
  line-height: 1; height:auto;
  padding: .2rem .35rem;       /* keep hit area steady */
 border-radius:8px; cursor:pointer;
 }
.announce__close:hover{background:rgba(255,255,255,.10)}
.announce__close:focus-visible{outline:3px solid rgba(130,194,255,.6); outline-offset:2px}

 /* Two-tone spans */
.announce__label{ font-weight:800; color:var(--ann-label, currentColor); line-height:1.25; }
.announce__desc { color:var(--ann-desc,  currentColor); line-height:1.25; }
/* Variants */
.announce--info{
  --ann-bg1:#0b1b2b; --ann-bg2:#0f263d; --ann-accent:rgba(28,95,151,.28); --ann-ink:#eaf2ff;
}
.announce--beta{
  --ann-bg1:#1b1740; --ann-bg2:#241a67; --ann-accent:rgba(111,121,255,.28); --ann-ink:#eef0ff;
}
.announce--success{
  --ann-bg1:#0c3b35; --ann-bg2:#0a2a25; --ann-accent:rgba(20,184,166,.22); --ann-ink:#eafff7;
}
.announce--warning{
  --ann-bg1:#5b360c; --ann-bg2:#482a09; --ann-accent:rgba(255,166,0,.26); --ann-ink:#fff6e6;
}
.announce--danger{
  --ann-bg1:#3a0e12; --ann-bg2:#2a0a0d; --ann-accent:rgba(239,68,68,.28); --ann-ink:#ffecec;
}

/* NEW: Light variant (for notice_duo) */
.announce--light{
  --ann-bg1:#f7fbff;
  --ann-bg2:#eef6ff;
  --ann-accent:rgba(80,140,220,.12);
  --ann-ink:#0f2740;
  color: var(--ann-ink);
  border-bottom:1px solid rgba(0,0,0,.05);
}

/* ===== Marquee ===== */
.announce__scroller{ overflow:hidden; flex:1 1 auto; min-width:0; }
.announce__track{
   display:flex; gap:40px;
   will-change: transform;
   animation: marquee-move var(--marquee-duration, 20s) linear infinite;
  line-height:1.25; /* keep same line-height inside scroller */
 }
.announce__chunk{ flex:0 0 auto; white-space:nowrap; }

/* Pause on hover/focus */
.announce:hover .announce__track,
.announce:focus-within .announce__track { animation-play-state: paused; }

/* Respect reduced motion */
@media (prefers-reduced-motion: reduce){
  .announce__track{ animation: none !important; }
}

@keyframes marquee-move{
  to { transform: translateX(var(--marquee-to, -50%)); }
}
</style>
